/**
 * Hook for managing story creation session
 *
 * Handles session creation, retrieval, and updates throughout
 * the story creation wizard flow.
 */

import { useCallback, useState } from 'react'

import { isInsufficientCreditsError } from '@/shared/api/errors'
import {
  getSessionsRetrieveQueryKey, useSessionsCreate, useSessionsPartialUpdate, useSessionsRetrieve, useSessionsSubmitCreate,
} from '@/shared/api/generated/story-sessions/story-sessions'
import { SessionSubmitResponse, StoryCreationSession } from '@/shared/api/models'
import { useInsufficientCreditsDialog } from '@/shared/hooks/useInsufficientCreditsDialog'
import { useQueryClient } from '@tanstack/react-query'

const SESSION_STORAGE_KEY = 'pixie_creation_session_uuid'

interface UseStoryCreationSessionOptions {
  readerProfileId?: number
}

interface UseStoryCreationSessionReturn {
  session: StoryCreationSession | null
  sessionUuid: string | null
  isLoading: boolean
  isCreating: boolean
  isUpdating: boolean
  isSubmitting: boolean
  error: string | null
  isPremadePath: boolean

  // Actions
  createSession: (options?: {
    initialPrompt?: string
    characterIds?: number[]
    artStyleId?: number
    languageCode?: string
  }) => Promise<StoryCreationSession | null>
  updateCharacters: (characterIds: number[]) => Promise<StoryCreationSession | null>
  updateArtStyle: (artStyleId: number) => Promise<StoryCreationSession | null>
  updatePrintPreset: (presetId: number) => Promise<StoryCreationSession | null>
  updateLanguage: (languageCode: string) => Promise<StoryCreationSession | null>
  updatePremadeStory: (premadeStoryId: number | null) => Promise<StoryCreationSession | null>
  submitSession: () => Promise<SessionSubmitResponse>
  refetchSession: () => Promise<void>
  clearSession: () => void
}

function getStoredSessionUuid(): string | null {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY)
  } catch {
    return null
  }
}

function storeSessionUuid(uuid: string | null) {
  try {
    if (uuid) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, uuid)
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_KEY)
    }
  } catch {
    // sessionStorage unavailable (SSR, private browsing)
  }
}

/**
 * Hook to manage story creation session lifecycle
 */
export function useStoryCreationSession({
  readerProfileId,
}: UseStoryCreationSessionOptions = {}): UseStoryCreationSessionReturn {
  const queryClient = useQueryClient()
  const { showInsufficientCreditsDialog } = useInsufficientCreditsDialog()
  const [sessionUuid, setSessionUuidState] = useState<string | null>(getStoredSessionUuid)
  const [error, setError] = useState<string | null>(null)
  const [selectedPremadeStoryId, setSelectedPremadeStoryId] = useState<number | null>(null)

  const setSessionUuid = useCallback((uuid: string | null) => {
    setSessionUuidState(uuid)
    storeSessionUuid(uuid)
  }, [])

  // Session retrieval query
  const {
    data: session,
    isLoading,
    refetch,
  } = useSessionsRetrieve(sessionUuid || '', {
    query: {
      enabled: !!sessionUuid,
    },
  })

  // Mutations
  const { mutateAsync: createMutation, isPending: isCreating } = useSessionsCreate()
  const { mutateAsync: updateMutation, isPending: isUpdating } = useSessionsPartialUpdate()
  const { mutateAsync: submitMutation, isPending: isSubmitting } = useSessionsSubmitCreate()

  /**
   * Create a new session
   */
  const createSession = useCallback(
    async (options?: {
      initialPrompt?: string
      characterIds?: number[]
      artStyleId?: number
      languageCode?: string
    }) => {
      if (!readerProfileId) {
        setError('Reader profile ID is required')

        return null
      }

      setError(null)

      try {
        const newSession = await createMutation({
          data: {
            reader_profile_id: readerProfileId,
            initial_prompt: options?.initialPrompt,
            character_ids: options?.characterIds,
            art_style_id: options?.artStyleId,
            language_code: options?.languageCode,
          },
        })

        setSessionUuid(newSession.uuid)
        queryClient.setQueryData(getSessionsRetrieveQueryKey(newSession.uuid), newSession)

        return newSession
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create session'

        setError(message)

        return null
      }
    },
    [readerProfileId, createMutation, setSessionUuid, queryClient],
  )

  /**
   * Update character selection
   */
  const updateCharacters = useCallback(
    async (characterIds: number[]) => {
      if (!sessionUuid) {
        setError('No active session')

        return null
      }

      setError(null)

      try {
        const updatedSession = await updateMutation({
          uuid: sessionUuid,
          data: {
            character_ids: characterIds,
          },
        })

        queryClient.setQueryData(getSessionsRetrieveQueryKey(sessionUuid), updatedSession)

        return updatedSession
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update characters'

        setError(message)

        return null
      }
    },
    [sessionUuid, updateMutation, queryClient],
  )

  /**
   * Update art style selection
   */
  const updateArtStyle = useCallback(
    async (artStyleId: number) => {
      if (!sessionUuid) {
        setError('No active session')

        return null
      }

      setError(null)

      try {
        const updatedSession = await updateMutation({
          uuid: sessionUuid,
          data: {
            art_style_id: artStyleId,
          },
        })

        queryClient.setQueryData(getSessionsRetrieveQueryKey(sessionUuid), updatedSession)

        return updatedSession
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update art style'

        setError(message)

        return null
      }
    },
    [sessionUuid, updateMutation, queryClient],
  )

  /**
   * Update print preset selection
   */
  const updatePrintPreset = useCallback(
    async (presetId: number) => {
      if (!sessionUuid) {
        setError('No active session')

        return null
      }

      setError(null)

      try {
        const updatedSession = await updateMutation({
          uuid: sessionUuid,
          data: {
            print_preset_id: presetId,
          },
        })

        queryClient.setQueryData(getSessionsRetrieveQueryKey(sessionUuid), updatedSession)

        return updatedSession
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update book format'

        setError(message)

        return null
      }
    },
    [sessionUuid, updateMutation, queryClient],
  )

  /**
   * Update language selection
   */
  const updateLanguage = useCallback(
    async (languageCode: string) => {
      if (!sessionUuid) {
        setError('No active session')

        return null
      }

      setError(null)

      try {
        const updatedSession = await updateMutation({
          uuid: sessionUuid,
          data: {
            language_code: languageCode,
          },
        })

        queryClient.setQueryData(getSessionsRetrieveQueryKey(sessionUuid), updatedSession)

        return updatedSession
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update language'

        setError(message)

        return null
      }
    },
    [sessionUuid, updateMutation, queryClient],
  )

  /**
   * Select or clear a premade story for this session
   */
  const updatePremadeStory = useCallback(
    async (premadeStoryId: number | null) => {
      if (!sessionUuid) {
        setError('No active session')

        return null
      }

      setError(null)

      try {
        const updatedSession = await updateMutation({
          uuid: sessionUuid,
          data: {
            premade_story_id: premadeStoryId,
          },
        })

        setSelectedPremadeStoryId(premadeStoryId)
        queryClient.setQueryData(getSessionsRetrieveQueryKey(sessionUuid), updatedSession)

        return updatedSession
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update premade story'

        setError(message)

        return null
      }
    },
    [sessionUuid, updateMutation, queryClient],
  )

  /**
   * Submit session for full story generation.
   * Throws on error so callers can distinguish success from failure.
   */
  const submitSession = useCallback(async (): Promise<SessionSubmitResponse> => {
    if (!sessionUuid) {
      const msg = 'No active session'

      setError(msg)
      throw new Error(msg)
    }

    setError(null)

    try {
      const result = await submitMutation({ uuid: sessionUuid })

      return result
    } catch (err: unknown) {
      if (isInsufficientCreditsError(err)) {
        setError('Insufficient credits. Please purchase more credits to continue.')
        showInsufficientCreditsDialog()
      } else {
        const message = err instanceof Error ? err.message : 'Failed to submit session'

        setError(message)
      }

      throw err
    }
  }, [sessionUuid, submitMutation, showInsufficientCreditsDialog])

  /**
   * Refetch session data
   */
  const refetchSession = useCallback(async () => {
    if (sessionUuid) {
      await refetch()
    }
  }, [sessionUuid, refetch])

  /**
   * Clear current session
   */
  const clearSession = useCallback(() => {
    setSessionUuid(null)
    setError(null)
    setSelectedPremadeStoryId(null)
  }, [setSessionUuid])

  return {
    session: session || null,
    sessionUuid,
    isLoading,
    isCreating,
    isUpdating,
    isSubmitting,
    error,
    isPremadePath: selectedPremadeStoryId !== null,
    createSession,
    updateCharacters,
    updateArtStyle,
    updatePrintPreset,
    updateLanguage,
    updatePremadeStory,
    submitSession,
    refetchSession,
    clearSession,
  }
}
